import { ChevronRightIcon } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { env } from 'src/config/env'
import { paths } from 'src/config/paths'
import LogoSrc from 'src/shared/components/icons/logos/logo.png'
import AnimateInView from 'src/shared/components/ui/animation/animate-in-view'
import { Button } from 'src/shared/components/ui/buttons/button'
import { WebpImage } from 'src/shared/components/ui/image/webp-image'
import { CONSTANTS } from 'src/shared/constants/constants'
import { cn } from 'src/shared/utils/cn'

import AppPreviewImageFallback from '../../home/assets/app-preview-image.png'
import AppPreviewImageWebp from '../../home/assets/app-preview-image.webp'
import EqualHousingOpportunityImage from '../../home/assets/equal-housing-opportunity.png'
import HomeCalculatorPopup from '../../home/components/home-calculator-popup'

function LinkContainer({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn('mb-9 flex break-inside-avoid flex-col gap-3 md:gap-4', className)}>{children}</div>
}

interface LinkItemHeaderProps {
  label: string
}

function LinkItemHeader({ label }: LinkItemHeaderProps) {
  return <p className="mb-1 text-[14px] font-medium leading-[1.6] text-white xs:text-[15px]">{label}</p>
}

interface LinkItemProps {
  href?: string
  label: string
  onClick?: () => void
  target?: string
}

function LinkItem({ href, label, onClick, target }: LinkItemProps) {
  return (
    <a
      href={href}
      onClick={onClick}
      target={target}
      className="group flex cursor-pointer flex-row items-center justify-center gap-1 text-[15px] font-medium leading-[1.4] tracking-[-0.24px] text-white/75 hover:text-white xs:text-[17px] md:justify-start"
    >
      {label}
      <ChevronRightIcon className="hidden size-4 opacity-0 transition-opacity group-hover:opacity-100 md:block" />
    </a>
  )
}

interface EnterpriseFooterProps {
  className?: string
}

export default function EnterpriseFooter({ className }: EnterpriseFooterProps) {
  const navigate = useNavigate()
  const [isCalculatorPopupOpen, setIsCalculatorPopupOpen] = useState(false)

  const downloadAppOnClick = () => {
    navigate(paths.downloadApp.getHref())
  }

  const professionalsScheduleDemoOnClick = () => {
    window.open(CONSTANTS.LINK_TO_DEMO_FOR_PROFESSIONALS, '_blank')
  }

  const enterprisesScheduleDemoOnClick = () => {
    window.open(CONSTANTS.LINK_TO_DEMO_FOR_ENTERPRISES, '_blank')
  }

  return (
    <footer
      className={cn(
        'w-full rounded-t-[40px] bg-primary pb-[50px] text-white xs:pb-[60px] md:rounded-t-[60px] md:pb-0',
        className,
      )}
    >
      <div className="mx-auto w-full max-w-[1470px] p-5 xs:px-8 sm:px-[60px] md:py-10 md:pt-[72px] xl:px-[85px]">
        <div className="mx-auto mb-[-120px] flex max-w-[1250px] translate-y-[-120px] flex-col gap-0 md:mb-0 md:translate-y-0 md:gap-7">
          <div className="flex flex-col-reverse items-center gap-[68px] md:flex-row md:justify-between md:gap-4">
            <div className="flex flex-1 flex-col items-center gap-9 md:items-start md:gap-[64px]">
              {/* Logo */}
              <div className="flex flex-row items-center gap-6">
                <div className="h-[30px] w-[145px]">
                  <img src={LogoSrc} alt="Conrad logo" className="size-full" />
                </div>
                <div className="hidden h-[16px] w-[1.5px] bg-[#EFEFEF] bg-opacity-25 md:block"></div>
              </div>
              {/* Links */}
              <div className="columns-1 items-center gap-5 self-stretch text-center xs:items-start md:text-left min-[950px]:columns-2 min-[1170px]:columns-3">
                <LinkContainer>
                  <LinkItem label="Buy" onClick={() => navigate(paths.root.getHref())} />
                  <LinkItem label="Sell" onClick={() => navigate(paths.root.getHref())} />
                  <LinkItem label="Lease" onClick={() => navigate(paths.root.getHref())} />
                  {/* <LinkItem label="Top agents" />
                  <LinkItem label="Top areas" /> */}
                </LinkContainer>
                <LinkContainer>
                  <LinkItemHeader label="RESOURCES" />
                  {/* <LinkItem label="Sign-up" /> */}
                  <LinkItem label="Download iOS App" onClick={downloadAppOnClick} />
                  <LinkItem label="Download Android App" onClick={downloadAppOnClick} />
                  <LinkItem label="Affordability calculator" onClick={() => setIsCalculatorPopupOpen(true)} />
                  <LinkItem label="Knowledge center" href={paths.blog.list.getHref()} target="_blank" />
                  <LinkItem label="Help Center" href={env.LINK_TO_HELP_CENTER} target="_blank" />
                </LinkContainer>
                <LinkContainer>
                  <LinkItemHeader label="FOR AGENTS & LOs" />
                  <LinkItem label="Home" onClick={() => navigate(paths.professionals.index.getHref())} />
                  <LinkItem label="Overview" onClick={() => navigate(paths.professionals.overview.getHref())} />
                  <LinkItem
                    label="Get more business"
                    onClick={() => navigate(paths.professionals.getMoreBusiness.getHref())}
                  />
                  <LinkItem label="Implement AI" onClick={() => navigate(paths.professionals.implementAi.getHref())} />
                  <LinkItem
                    label="Convert more leads"
                    onClick={() => navigate(paths.professionals.convertMoreLeads.getHref())}
                  />
                  <LinkItem
                    label="Run your business"
                    onClick={() => navigate(paths.professionals.runYourBusiness.getHref())}
                  />
                  <LinkItem
                    label="Grow your network"
                    onClick={() => navigate(paths.professionals.growYourNetwork.getHref())}
                  />
                  <LinkItem label="Schedule demo" onClick={professionalsScheduleDemoOnClick} />
                </LinkContainer>
                <LinkContainer>
                  <LinkItemHeader label="FOR COMPANIES" />
                  <LinkItem label="Home" onClick={() => navigate(paths.enterprise.getHref())} />
                  <LinkItem
                    label="Small companies"
                    onClick={() => navigate(paths.enterprise.solutions.smallCompanies.getHref())}
                  />
                  <LinkItem
                    label="Mid markets"
                    onClick={() => navigate(paths.enterprise.solutions.midMarkets.getHref())}
                  />
                  <LinkItem
                    label="Enterprises"
                    onClick={() => navigate(paths.enterprise.solutions.enterprises.getHref())}
                  />
                  <LinkItem
                    label="Switch to Conrad"
                    onClick={() => navigate(paths.enterprise.switchToConrad.getHref())}
                  />
                  <LinkItem label="Schedule demo" onClick={enterprisesScheduleDemoOnClick} />
                  {/* <LinkItem label="Pricing" />
                  <LinkItem label="Case studies" />
                  <LinkItem label="Schedule demo" /> */}
                </LinkContainer>
                {/* <LinkContainer>
                  <LinkItemHeader label="CONRAD" />
                  <LinkItem label="Integrations" />
                  <LinkItem label="Schedule demo" />
                  <LinkItem label="Sign-up" />
                  <LinkItem label="Changelog" />
                  <LinkItem label="Contact" />
                </LinkContainer> */}
                {/* Hack to make the FOR AGENTS & LOs and For Companies on the same column. */}
                <div className="pointer-events-none hidden h-[200px] opacity-0 md:flex"></div>
              </div>
            </div>
            {/* Download the app section */}
            <div className="top-[120px] flex max-w-[368px] flex-col items-center text-center md:sticky md:self-start">
              <AnimateInView
                className="w-full"
                threshold={0.8}
                initial={{ opacity: 0, y: 80 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="relative h-[250px] w-full rounded-[20px] min-[370px]:h-[290px] xs:h-[443px] xs:w-[368px]">
                  <WebpImage
                    src={AppPreviewImageWebp}
                    fallbackSrc={AppPreviewImageFallback}
                    alt="The home page of Conrad's mobile app, it shows claim leads and pending businesses."
                    className="object-cover object-top"
                  />
                  <div className="absolute inset-0 -bottom-px top-[70%] z-10 bg-gradient-to-b from-[#1B3029]/0 to-[#1B3029] xs:top-[50%]"></div>
                </div>
              </AnimateInView>
              <p className="mt-[18px] text-[16px] font-medium leading-normal text-white/75">
                {'One app for your entire real estate business.'}
              </p>
              <Button
                variant="default"
                className="mt-[14px] h-[50px] w-full max-w-[173px] rounded-full bg-white px-[30px] text-[15px] font-medium tracking-[-0.18px] text-primary min-[370px]:text-[16px]"
                onClick={downloadAppOnClick}
              >
                Download now
              </Button>
            </div>
          </div>
          {/* Copyright and legal section */}
          <div className="flex flex-col gap-[27px] md:gap-2">
            <div className="flex flex-col items-center justify-between gap-3 pb-2 pt-3 text-center text-[14px] tracking-[-0.288px] text-white/75 xs:gap-4 xs:pb-3 xs:pt-8 xs:text-base md:flex-row md:text-left">
              <span>© 2025 Ask Conrad, Inc. All Rights Reserved.</span>
              <div className="flex flex-row items-center gap-4">
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={env.LINK_TO_TERMS_OF_USE}
                  className="hover:underline"
                >
                  Terms of Service
                </a>
                <div className="h-[16px] w-[1.5px] bg-[#EFEFEF] bg-opacity-25"></div>
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={env.LINK_TO_PRIVACY_POLICY}
                  className="hover:underline"
                >
                  Privacy Policy
                </a>
              </div>
            </div>
            <div className="flex flex-col items-center gap-4 text-center md:flex-row md:text-left">
              <img src={EqualHousingOpportunityImage} alt="Equal Housing Opportunity" className="h-[46px] w-[48px]" />
              <p className="text-[13px] font-normal leading-[1.6] text-white/75 xs:text-[14px] xs:leading-[1.43]">
                {
                  'We are committed to compliance with all federal, state, and local fair housing laws. We do not discriminate against any person based on race, color, religion, sex (including gender identity and sexual orientation), handicap, familial status, or national origin.'
                }
              </p>
            </div>
          </div>
        </div>
      </div>
      <HomeCalculatorPopup isOpen={isCalculatorPopupOpen} setOpen={setIsCalculatorPopupOpen} />
    </footer>
  )
}
